import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import vgToast from "utils/toast";
import { Product } from "services/products.service";
import { addProductLines, MAX_LINE_QUANTITY } from "services/invoices.service";

type Props = {
  open: boolean;
  orgId: number;
  invoiceId: number;
  products: Product[];
  fallbackCurrency?: number;
  onCancel: () => void;
  onAdded: () => void;
};

/**
 * Adds a product to an invoice.
 *
 * The unit price is prefilled from the catalogue and editable, so a clinic can
 * discount a line without editing the product everyone else sells. Quantity
 * writes that many rows - see addProductLines - which is why it is bounded here
 * as well as there: the person typing should see the limit, not discover it.
 */
const AddProductLine = ({
  open,
  orgId,
  invoiceId,
  products,
  fallbackCurrency,
  onCancel,
  onAdded,
}: Props) => {
  const { getAccessTokenSilently } = useAuth0();

  const [productId, setProductId] = React.useState("");
  const [quantity, setQuantity] = React.useState("1");
  const [unitAmount, setUnitAmount] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const chosen = products.find((p) => String(p.productId) === productId);

  React.useEffect(() => {
    if (!open) return;
    setProductId("");
    setQuantity("1");
    setUnitAmount("");
    setError(null);
  }, [open]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!chosen) {
      setError("Choose a product.");
      return;
    }

    const qty = Number(quantity);
    if (!Number.isInteger(qty) || qty < 1) {
      setError("Quantity must be a whole number, at least 1.");
      return;
    }
    if (qty > MAX_LINE_QUANTITY) {
      setError(`Quantity is limited to ${MAX_LINE_QUANTITY} per line.`);
      return;
    }

    const amount = Number(unitAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      setError("Enter a unit price.");
      return;
    }

    setError(null);
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await addProductLines(token, {
        orgId,
        invoiceId,
        productId: chosen.productId,
        unitAmount: amount,
        currency: chosen.salesCurrency ?? fallbackCurrency ?? 0,
        quantity: qty,
      });

      if (apiError) {
        // Partial failure lands here too, and its message says how many were
        // added. Reloading regardless is the honest move: the invoice changed.
        vgToast.error(apiError.message);
        onAdded();
        return;
      }

      vgToast.success(qty > 1 ? `${qty} added.` : "Item added.");
      onAdded();
    } catch (err) {
      vgToast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const lineTotal = Number(unitAmount) * Number(quantity);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 print:hidden">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/35 backdrop-blur-[2px]"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />

      <form
        onSubmit={submit}
        className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-[0_18px_50px_-12px_rgba(15,23,42,0.28)]"
      >
        <div className="border-b border-slate-100 bg-slate-50/70 px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900">Add an item</h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            Adds a product from the catalogue to this invoice.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-2">
          {products.length === 0 && (
            <p className="sm:col-span-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              This clinic has no products yet. Add one under Products before
              billing for it.
            </p>
          )}

          <div className="sm:col-span-2">
            <Select
              label="Product"
              placeholder="Choose a product"
              options={products.map((p) => ({
                title: p.name,
                value: String(p.productId),
              }))}
              value={productId}
              onChange={(e) => {
                // Picking a product sets its price; the user can still edit it
                // afterwards. Switching product replaces the previous price.
                const picked = products.find(
                  (p) => String(p.productId) === e.target.value
                );
                setProductId(e.target.value);
                setUnitAmount(
                  picked?.salesPrice != null ? String(picked.salesPrice) : ""
                );
              }}
            />
          </div>

          <Input
            type="number"
            label="Quantity"
            value={quantity}
            min={1}
            max={MAX_LINE_QUANTITY}
            onChange={(e) => setQuantity(e.target.value)}
          />
          <Input
            type="number"
            label="Unit price"
            value={unitAmount}
            onChange={(e) => setUnitAmount(e.target.value)}
          />

          {Number.isFinite(lineTotal) && lineTotal > 0 && (
            <p className="sm:col-span-2 text-sm text-slate-500">
              Line total: {lineTotal.toFixed(2)}
            </p>
          )}

          {error && <p className="sm:col-span-2 text-sm text-red-500">{error}</p>}
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <Button color="Secondary" onClick={onCancel} disabled={busy}>
            <span>Cancel</span>
          </Button>
          <Button
            color="Primary"
            type="submit"
            disabled={busy || products.length === 0}
          >
            <span>{busy ? "Adding…" : "Add to invoice"}</span>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddProductLine;
